import Highcharts, { LegendOptions } from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import { Tile } from '@op/opds.opux';
import { space } from '@op/opds.tokens';
import { Message, useMessageGetter } from '@messageformat/react';
import { IconInformation } from '@op/opds.opux-icons/indicator';
import { getChartOptionsWithOverrides } from '../../util/defaultOptions';
import styles from './StockPieChart.module.scss';
import { useIsMobile } from '../../util/mediaQuery';

const StockPieChart = (): JSX.Element => {
  const isMobile = useIsMobile();
  const getText = useMessageGetter('course2.part2.stock_pie_graph');
  const values = [
    [`${getText('finland', { value: 0.3 })}`, 0.3],
    [`${getText('united_states', { value: 46.2 })}`, 46.2],
    [`${getText('china', { value: 10.6 })}`, 10.6],
    [`${getText('europe', { value: 9.1 })}`, 9.1],
    [`${getText('japan', { value: 4.7 })}`, 4.7],
    [`${getText('great_britain', { value: 2.7 })}`, 2.7],
    [`${getText('others', { value: 26.7 })}`, 26.7],
  ];
  const legend: LegendOptions = {
    enabled: true,
    events: {
      itemClick: () => false,
    },
    layout: 'vertical',
    align: 'right',
    verticalAlign: 'middle',
  };
  const mobileLegend: LegendOptions = {
    ...legend,
    layout: 'horizontal',
    align: 'center',
    verticalAlign: 'bottom',
  };
  const options = getChartOptionsWithOverrides({
    accessibility: {
      enabled: false,
    },
    legend: isMobile ? mobileLegend : legend,
    title: {
      text: undefined,
    },
    tooltip: {
      enabled: true,
      format: '{point.name}',
    },
    series: [
      {
        type: 'pie',
        data: values,
        colors: [
          '#EE5A00', // Color for Finland
          '#BEBEBE', // Color for United States
          '#6C6C6C', // Color for China
          '#078FE7', // Color for Europe
          '#D2A70F', // Color for Japan
          '#009F33', // Color for Great Britain
          '#CD212E', // Color for Others
        ],
        innerSize: '60%',
        showInLegend: true,
        dataLabels: {
          enabled: false,
        },
      },
    ],
    responsive: {
      rules: [
        {
          condition: { maxWidth: 320 },
          chartOptions: {
            legend: {
              layout: 'vertical',
            },
          },
        },
      ],
    },
  });

  return (
    <div className="investoracademy-graph-wrapper">
      <Tile>
        <figure className={styles.figure}>
          <figcaption>{getText('title')}</figcaption>
          <HighchartsReact
            highcharts={Highcharts}
            options={options}
            containerProps={{
              'aria-labelledby': 'stock-pie-graph-description',
              className: styles.chart,
            }}
          />
          <p id="stock-pie-graph-description" style={{ display: 'none' }}>
            <Message id="course2.part2.stock_pie_graph.alt_text" />
          </p>
          <div className="sr-only">
            <p>
              <Message id="course2.part2.stock_pie_graph.list_description" />
            </p>
            <ul>
              {values.map(([label]) => (
                <li key={label}>{label}</li>
              ))}
            </ul>
          </div>
        </figure>
      </Tile>
      <p
        className="opds-styles-v1-text-label-small"
        style={{ display: 'flex', paddingBottom: space.default }}
      >
        <IconInformation />
        <Message id="course2.part2.stock_pie_graph.sources" />
      </p>
    </div>
  );
};

export default StockPieChart;
